import React, { FC, useState, HTMLProps } from 'react';
import Spinner from '@atlaskit/spinner';
import Badge from '@atlaskit/badge';
import { CapabilityDTO, CapabilitySummaryDTOFromJSON } from '@atlassiansox/phobos-client-ts';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ExperienceSuccess as UFOExperienceSuccess } from '@atlassian/ufo';

import { reportCapabilityListExperience } from '@/common/ufo/experiences';
import {
  flexStart,
  marginAuto,
  spaceTopComfortable,
  extendWidth,
  textAlignCenter,
  flexCenter,
  flexDirectionColumn,
  spaceTopCozy,
  spaceGapCozy,
} from '@/styles';
import { BreachCount, KeyValue } from '@/components/primitive';
import { ExperienceErrorBoundary } from '@/components/primitive/error-boundary';
import { queryStore } from '@/components/composed/catalog-search-bar/stores/query';
import { filterStore, getSLOFilterQuery, useFetchErrorController } from '@/controllers';
import { apiFetch } from '@/services';
import { PrimaryType } from '@/meta';

import * as queryUtils from '../catalog-search-bar/services/utils';
import { Params } from '../catalog-search-bar/types';

import * as styles from './styles';
import CapabilityListItem from './capability-list-item';

type CapabilityListProps = {
  isLoading: boolean;
  totalElements: number;
  rows: CapabilityDTO[] | undefined;
  experienceSeq: number;
  entityType?: PrimaryType;
} & Pick<HTMLProps<HTMLDivElement>, 'className'>;

const CapabilityList: FC<CapabilityListProps> = ({
  rows,
  experienceSeq,
  totalElements,
  isLoading,
  className,
  entityType,
}) => {
  const [openRows, setSelected] = useState<string[]>([]);

  const { state: queryState } = React.useContext(queryStore);
  const { state: filterState } = React.useContext(filterStore);

  const { publicId: paramPublicId } = useParams<{ publicId: string }>();
  let queryParams: Params;

  switch (entityType) {
    case 'product':
      queryParams = { product: [{ publicId: paramPublicId }] } as Params;
      break;
    case 'team':
      queryParams = { team: [{ publicId: paramPublicId }] } as Params;
      break;
    default:
      queryParams = queryState.params;
      break;
  }
  const capabilityParams = queryUtils.mapParamsToAPIQuery(queryParams, true);
  const capabilityFilter = getSLOFilterQuery(filterState.slo);
  const capabilityAPIQuery = `/capability/summary?${capabilityParams}${capabilityFilter}`;

  const { onError } = useFetchErrorController();
  const { isInitialLoading: isSummaryLoading, data: rawData } = useQuery(
    ['capability', 'summary', capabilityAPIQuery],
    () => apiFetch(capabilityAPIQuery),
    { onError }
  );

  const data = CapabilitySummaryDTOFromJSON(rawData);
  // TODO: why even passing in totalElements instead of using summary api total?
  // To be fixed when summary endpoint filter is fixed
  const showSummaryBar: boolean = totalElements > 0;

  return (
    <ExperienceErrorBoundary>
      <div className={className}>
        {showSummaryBar && (
          <div css={styles.listRowLayout}>
            <div css={[flexStart, extendWidth]}>
              <h3>Capabilities</h3>
              <KeyValue css={styles.listInfoGapStyles} title="With open SLO breaches">
                <BreachCount count={data?.breaching ?? 0} />
              </KeyValue>
              <KeyValue title="Total">
                <Badge max={Infinity}>{data?.total ?? 0}</Badge>
              </KeyValue>
            </div>
          </div>
        )}

        <div css={styles.tableStyles}>
          {/* eslint-disable no-nested-ternary */}
          {isLoading ? (
            <div css={[spaceTopComfortable, textAlignCenter]}>
              <Spinner css={marginAuto} size="large" />
            </div>
          ) : rows?.length ? (
            <>
              {rows.map(
                ({
                  publicId,
                  name,
                  team,
                  product,
                  importance,
                  breaches,
                  sloCount,
                  qualityIssues,
                }) => (
                  <CapabilityListItem
                    key={publicId}
                    name={name}
                    team={team}
                    product={product}
                    publicId={publicId}
                    importance={importance}
                    breaches={breaches}
                    sloCount={sloCount}
                    qualityIssues={qualityIssues}
                    isSelected={openRows.includes(publicId)}
                    onSelect={() =>
                      setSelected(currentlySelected => {
                        if (currentlySelected.includes(publicId)) {
                          return currentlySelected.filter(id => id !== publicId);
                        }
                        return currentlySelected.concat(publicId);
                      })
                    }
                    entityType={entityType}
                  />
                )
              )}
            </>
          ) : (
            <div css={[flexCenter, flexDirectionColumn, spaceTopCozy, spaceGapCozy]}>
              <img src="/static/not-found.svg" alt="not-found" />
              <p>No capabilities found for the selected search</p>
            </div>
          )}
          {!isLoading && !isSummaryLoading && (
            <UFOExperienceSuccess key={experienceSeq} experience={reportCapabilityListExperience} />
          )}
        </div>
      </div>
    </ExperienceErrorBoundary>
  );
};

export default CapabilityList;
